/**
 * Navigation Manager — Moon Moon Agency
 * Header sticky + hide-on-scroll, mega menus desktop (hover/click intent),
 * mobile drawer (volet 1 + volet 2 avec slide horizontal), focus trap.
 */

/*********************************************************************************************
 * 📌 HEADER SCROLL MANAGER — sticky + hide-on-scroll
 */
const HeaderScrollManager = {
	config: {
		selectors: {
			header: '.mm-section-header',
		},
		classes: {
			hidden: 'is-scroll-hidden',
			scrolled: 'is-scrolled',
			home: 'is-home',
		},
		threshold: 80,
		scrolledThreshold: 40,
	},

	state: {
		lastY: 0,
		locked: false, // Bloqué quand un mega/mobile est ouvert
	},

	init() {
		const header = document.querySelector(this.config.selectors.header);
		if (!header) return;
		this.header = header;
		this.state.lastY = window.scrollY;

		// Detection page d'accueil — pour le mode header transparent mobile
		this.isHome = window.location.pathname === '/' || window.location.pathname === '/index';
		if (this.isHome) this.header.classList.add(this.config.classes.home);

		this._updateScrolledClass();
		window.addEventListener('scroll', this._onScroll.bind(this), { passive: true });
		window.log('info', '📌 [HEADER_SCROLL]', 'Initialized');
	},

	_onScroll() {
		if (this.state.locked) {
			this._updateScrolledClass();
			return;
		}

		const y = window.scrollY;
		const delta = y - this.state.lastY;
		this.state.lastY = y;

		this._updateScrolledClass();

		if (y < this.config.threshold) {
			this.header.classList.remove(this.config.classes.hidden);
			return;
		}
		if (delta > 4) {
			this.header.classList.add(this.config.classes.hidden);
		} else if (delta < -4) {
			this.header.classList.remove(this.config.classes.hidden);
		}
	},

	_updateScrolledClass() {
		if (window.scrollY > this.config.scrolledThreshold) {
			this.header.classList.add(this.config.classes.scrolled);
		} else {
			this.header.classList.remove(this.config.classes.scrolled);
		}
	},

	lock() {
		this.state.locked = true;
		this.header?.classList.remove(this.config.classes.hidden);
	},

	unlock() {
		this.state.locked = false;
	},
};

/*********************************************************************************************
 * 🎯 MEGA MENU MANAGER — desktop hover/click avec intent
 */
const MegaMenuManager = {
	config: {
		selectors: {
			wrap: '[data-mm-mega-wrap]',
			trigger: '[data-mm-mega-trigger]',
			panel: '[data-mm-mega]',
			header: '.mm-section-header',
			overlay: '[data-mm-mega-overlay]',
		},
		classes: {
			open: 'is-open',
		},
		hoverDelay: 120,
		closeDelay: 200,
	},

	state: {
		openWrap: null,
		hoverTimer: null,
	},

	init() {
		const wraps = document.querySelectorAll(this.config.selectors.wrap);
		if (!wraps.length) return;
		this.wraps = Array.from(wraps);
		this.overlay = this._ensureOverlay();

		this._bindEvents();
		this._bindTabs();
		window.log('info', '🎯 [MEGA_MENU]', `Initialized (${this.wraps.length} wraps)`);
	},

	/**
	 * Injecte l'overlay comme enfant direct de <body> (sinon il subit le
	 * stacking context de .mm-section-header sticky z:60 et se retrouve au-
	 * dessus du header au lieu d'en dessous). Idempotent.
	 */
	_ensureOverlay() {
		let overlay = document.querySelector(this.config.selectors.overlay);
		if (overlay && overlay.parentElement !== document.body) {
			overlay.remove();
			overlay = null;
		}
		if (!overlay) {
			overlay = document.createElement('div');
			overlay.className = 'mm-mega-overlay';
			overlay.setAttribute('data-mm-mega-overlay', '');
			overlay.setAttribute('aria-hidden', 'true');
			overlay.hidden = true;
			document.body.appendChild(overlay);
		}
		return overlay;
	},

	_showOverlay() {
		if (!this.overlay) return;
		this.overlay.hidden = false;
		requestAnimationFrame(() => this.overlay.classList.add(this.config.classes.open));
	},

	_hideOverlay() {
		if (!this.overlay) return;
		this.overlay.classList.remove(this.config.classes.open);
		setTimeout(() => {
			if (!this.overlay.classList.contains(this.config.classes.open)) {
				this.overlay.hidden = true;
			}
		}, 400);
	},

	/**
	 * Tab switching à l'intérieur d'un mega style 1 avec sous-niveau 3.
	 * Délégation : un clic sur [data-mm-mega-tab] active l'onglet, révèle le
	 * panel correspondant [data-mm-mega-panel] et le CTA associé [data-mm-mega-tab-cta].
	 */
	_bindTabs() {
		document.addEventListener('click', (e) => {
			const tab = e.target.closest('[data-mm-mega-tab]');
			if (!tab) return;
			const panelRoot = tab.closest('[data-mm-mega]');
			if (!panelRoot) return;

			const target = tab.getAttribute('data-mm-mega-tab');

			panelRoot.querySelectorAll('[data-mm-mega-tab]').forEach((t) => {
				const active = t === tab;
				t.classList.toggle('is-active', active);
				t.setAttribute('aria-selected', String(active));
			});
			panelRoot.querySelectorAll('[data-mm-mega-panel]').forEach((p) => {
				p.classList.toggle('is-active', p.getAttribute('data-mm-mega-panel') === target);
			});
			panelRoot.querySelectorAll('[data-mm-mega-tab-cta]').forEach((c) => {
				c.classList.toggle('is-active', c.getAttribute('data-mm-mega-tab-cta') === target);
			});
		});
	},

	_bindEvents() {
		this.wraps.forEach((wrap) => {
			const trigger = wrap.querySelector(this.config.selectors.trigger);
			const panel = wrap.querySelector(this.config.selectors.panel);
			if (!trigger || !panel) return;

			// Hover intent
			wrap.addEventListener('mouseenter', () => {
				if (window.isMobile() || window.mmIsTouchDevice()) return;
				clearTimeout(this.state.hoverTimer);
				this.state.hoverTimer = setTimeout(() => this.open(wrap, trigger, panel), this.config.hoverDelay);
			});
			wrap.addEventListener('mouseleave', () => {
				if (window.isMobile() || window.mmIsTouchDevice()) return;
				clearTimeout(this.state.hoverTimer);
				this.state.hoverTimer = setTimeout(() => this.close(wrap, trigger, panel), this.config.closeDelay);
			});

			// Click (touch) or Enter
			trigger.addEventListener('click', (e) => {
				// Sur desktop pointer, laisse le hover gérer. Sur touch, empêche navigation et toggle.
				if (window.mmIsTouchDevice()) {
					e.preventDefault();
					this._toggle(wrap, trigger, panel);
				}
			});

			trigger.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this._toggle(wrap, trigger, panel);
				}
				if (e.key === 'Escape') {
					this.close(wrap, trigger, panel);
					trigger.focus();
				}
			});

			wrap.addEventListener('focusout', (e) => {
				if (this.state.openWrap === wrap && e.relatedTarget && !wrap.contains(e.relatedTarget)) {
					this.close(wrap, trigger, panel);
				}
			});
		});

		// Close on outside click (dont overlay click)
		document.addEventListener('click', (e) => {
			if (!this.state.openWrap) return;
			if (this.state.openWrap.contains(e.target)) return;
			const trigger = this.state.openWrap.querySelector(this.config.selectors.trigger);
			const panel = this.state.openWrap.querySelector(this.config.selectors.panel);
			this.close(this.state.openWrap, trigger, panel);
		});

		// Escape
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && this.state.openWrap) {
				const trigger = this.state.openWrap.querySelector(this.config.selectors.trigger);
				const panel = this.state.openWrap.querySelector(this.config.selectors.panel);
				this.close(this.state.openWrap, trigger, panel);
				trigger?.focus();
			}
		});
	},

	_toggle(wrap, trigger, panel) {
		if (this.state.openWrap === wrap) {
			this.close(wrap, trigger, panel);
		} else {
			this.open(wrap, trigger, panel);
		}
	},

	open(wrap, trigger, panel) {
		// Ferme un autre wrap ouvert
		if (this.state.openWrap && this.state.openWrap !== wrap) {
			const otherTrigger = this.state.openWrap.querySelector(this.config.selectors.trigger);
			const otherPanel = this.state.openWrap.querySelector(this.config.selectors.panel);
			this.close(this.state.openWrap, otherTrigger, otherPanel);
		}

		panel.hidden = false;
		// Reflow pour permettre la transition
		requestAnimationFrame(() => {
			panel.classList.add(this.config.classes.open);
			trigger.classList.add(this.config.classes.open);
			trigger.setAttribute('aria-expanded', 'true');
		});

		this.state.openWrap = wrap;
		HeaderScrollManager.lock();
		this._showOverlay();
		window.emit('global:mega-menu-opened', { index: wrap.dataset.mmMegaIndex });
	},

	close(wrap, trigger, panel) {
		if (!wrap) return;
		panel.classList.remove(this.config.classes.open);
		trigger?.classList.remove(this.config.classes.open);
		trigger?.setAttribute('aria-expanded', 'false');

		// Hide après transition
		setTimeout(() => {
			if (!panel.classList.contains(this.config.classes.open)) {
				panel.hidden = true;
			}
		}, 350);

		if (this.state.openWrap === wrap) {
			this.state.openWrap = null;
			HeaderScrollManager.unlock();
			this._hideOverlay();
		}
	},

	closeAll() {
		if (!this.state.openWrap) return;
		const trigger = this.state.openWrap.querySelector(this.config.selectors.trigger);
		const panel = this.state.openWrap.querySelector(this.config.selectors.panel);
		this.close(this.state.openWrap, trigger, panel);
	},
};

/*********************************************************************************************
 * 🎬 HOVER SCOPE MANAGER — propage l'état hover d'un ancêtre à des pills nested
 * Pattern MM : un ancêtre cliquable (card) avec data-btn-hover-scope déclenche
 * l'anim des CTAs qu'il contient (pill "Voir le pack", etc.).
 * Ici, on ajoute simplement une classe .is-hover-scope-active sur le scope au
 * mouseenter, retirée au mouseleave — le CSS peut la lire.
 */
const HoverScopeManager = {
	config: {
		selector: '[data-btn-hover-scope]',
		activeClass: 'is-hover-scope-active',
	},

	init() {
		document.addEventListener('mouseenter', (e) => {
			const scope = e.target.closest?.(this.config.selector);
			if (!scope || !scope.contains(e.target)) return;
			scope.classList.add(this.config.activeClass);
		}, true);
		document.addEventListener('mouseleave', (e) => {
			const scope = e.target.closest?.(this.config.selector);
			if (!scope || !scope.contains(e.target)) return;
			scope.classList.remove(this.config.activeClass);
		}, true);
		window.log('info', '🎬 [HOVER_SCOPE]', 'Initialized');
	},
};

/*********************************************************************************************
 * 📱 MOBILE NAV MANAGER — drawer + volet 2 slide
 */
const MobileNavManager = {
	config: {
		selectors: {
			burger: '[data-header-burger]',
			mnav: '[data-mm-mnav]',
			overlay: '[data-mm-mnav-overlay]',
			levels: '[data-mm-mnav-levels]',
			openTrigger: '[data-mm-mnav-open]',
			backBtn: '[data-mm-mnav-back]',
			closeBtn: '[data-mm-mnav-close]',
			page: '[data-mm-mnav-page]',
			level1: '.mm-mnav__level--1',
			level2: '.mm-mnav__level--2',
			topbar: '.mm-mnav__topbar',
		},
		classes: {
			open: 'is-open',
			level2: 'is-level2',
			active: 'is-active',
			stopScroll: 'stop-scrolling',
		},
	},

	state: {
		isOpen: false,
		activePage: null,
		focusTrap: null,
		triggerElement: null,
	},

	init() {
		const mnav = document.querySelector(this.config.selectors.mnav);
		const burger = document.querySelector(this.config.selectors.burger);
		if (!mnav || !burger) return;

		this.mnav = mnav;
		this.burger = burger;
		this.overlay = document.querySelector(this.config.selectors.overlay);
		this.levels = mnav.querySelector(this.config.selectors.levels);

		this._bindEvents();
		window.log('info', '📱 [MOBILE_NAV]', 'Initialized');
	},

	_bindEvents() {
		this.burger.addEventListener('click', () => this.toggle());
		this.overlay?.addEventListener('click', () => this.close());

		this.mnav.addEventListener('click', (e) => {
			const closeBtn = e.target.closest(this.config.selectors.closeBtn);
			if (closeBtn) {
				this.close();
				return;
			}
			const opener = e.target.closest(this.config.selectors.openTrigger);
			if (opener) {
				const target = opener.getAttribute('data-mm-mnav-open');
				this._openLevel2(target, opener);
				return;
			}
			const back = e.target.closest(this.config.selectors.backBtn);
			if (back) {
				this._closeLevel2();
				return;
			}
		});

		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && this.state.isOpen) {
				if (this.state.activePage) {
					this._closeLevel2();
				} else {
					this.close();
					this.burger?.focus();
				}
			}
		});
	},

	toggle() {
		this.state.isOpen ? this.close() : this.open();
	},

	open() {
		if (this.state.isOpen) return;

		this.state.triggerElement = document.activeElement;
		this.mnav.hidden = false;
		if (this.overlay) this.overlay.hidden = false;

		requestAnimationFrame(() => {
			this.mnav.classList.add(this.config.classes.open);
			this.overlay?.classList.add(this.config.classes.open);
			this.burger.setAttribute('aria-expanded', 'true');
			document.documentElement.classList.add(this.config.classes.stopScroll);
			this._setupFocusTrap();
			// Inert le contenu du body (main/footer) pour lecteurs d'écran
			document.querySelectorAll('main, footer').forEach(el => { el.inert = true; });
		});

		this.state.isOpen = true;
		HeaderScrollManager.lock();
		window.emit('global:mobile-menu-opened');
	},

	close(keepScrollLock = false) {
		if (!this.state.isOpen) return;

		this.mnav.classList.remove(this.config.classes.open);
		this.overlay?.classList.remove(this.config.classes.open);
		this.burger?.setAttribute('aria-expanded', 'false');
		document.querySelectorAll('[inert]').forEach(el => { el.inert = false; });

		// Reset level2 state — attend 450ms (durée transition drawer) pour retirer
		// is-level2 sinon le level2 glisserait horizontalement pendant que le drawer
		// se ferme (visible et disgracieux).
		setTimeout(() => {
			this.mnav.classList.remove(this.config.classes.level2);
			this.mnav.querySelectorAll(`.${this.config.classes.active}`).forEach(p => p.classList.remove(this.config.classes.active));
			this.state.activePage = null;
		}, 450);
		// Reset aria-expanded des openers
		this.mnav.querySelectorAll(`${this.config.selectors.openTrigger}[aria-expanded="true"]`).forEach(o => o.setAttribute('aria-expanded', 'false'));

		setTimeout(() => {
			if (!this.state.isOpen) {
				this.mnav.hidden = true;
				if (this.overlay) this.overlay.hidden = true;
			}
		}, 500);

		if (!keepScrollLock) {
			document.documentElement.classList.remove(this.config.classes.stopScroll);
		}
		this._removeFocusTrap();

		this.state.isOpen = false;
		HeaderScrollManager.unlock();
		window.emit('global:mobile-menu-closed');
	},

	_openLevel2(targetId, opener) {
		const page = this.mnav.querySelector(`${this.config.selectors.page}[data-mm-mnav-page="${targetId}"]`);
		if (!page) return;

		// Toggle .is-active sur la page cible (les autres perdent .is-active)
		this.mnav.querySelectorAll(this.config.selectors.page).forEach(p => {
			p.classList.toggle(this.config.classes.active, p === page);
		});

		// Ajoute .is-level2 sur le drawer (le container glisse via translateX(-50%))
		this.mnav.classList.add(this.config.classes.level2);
		opener?.setAttribute('aria-expanded', 'true');
		this._setLevel1Inert(true);
		this.state.activePage = page;

		// Scroll le level--2 en haut apres slide
		const level2 = this.mnav.querySelector(this.config.selectors.level2);
		if (level2) level2.scrollTop = 0;

		// Focus first interactive element de la page
		setTimeout(() => {
			const firstFocusable = page.querySelector('a, button');
			firstFocusable?.focus();
		}, 500);

		window.emit('global:mobile-submenu-opened', { target: targetId });
	},

	_closeLevel2() {
		if (!this.state.activePage) return;
		const opener = this.mnav.querySelector(`${this.config.selectors.openTrigger}[aria-expanded="true"]`);
		this.mnav.classList.remove(this.config.classes.level2);
		this.mnav.querySelectorAll(`${this.config.selectors.openTrigger}[aria-expanded="true"]`).forEach(o => o.setAttribute('aria-expanded', 'false'));
		this._setLevel1Inert(false);
		opener?.focus({ preventScroll: true });
		// Attend fin transition slide avant de retirer .is-active (evite un flash de contenu vide)
		setTimeout(() => {
			this.mnav.querySelectorAll(`.${this.config.classes.active}`).forEach(p => p.classList.remove(this.config.classes.active));
		}, 500);
		this.state.activePage = null;
		window.emit('global:mobile-submenu-closed');
	},

	_setLevel1Inert(on) {
		[this.config.selectors.level1, this.config.selectors.topbar].forEach((sel) => {
			const el = this.mnav.querySelector(sel);
			if (el) el.inert = on;
		});
	},

	_setupFocusTrap() {
		this._removeFocusTrap();

		const getFocusables = () => [...this.mnav.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
			.filter((el) => !el.closest('[inert]') && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
		if (!getFocusables().length) return;

		const handler = (e) => {
			if (e.key !== 'Tab') return;
			const focusables = getFocusables();
			if (!focusables.length) return;
			const first = focusables[0];
			const last = focusables[focusables.length - 1];
			if (!this.mnav.contains(document.activeElement)) {
				e.preventDefault();
				first.focus();
				return;
			}
			if (e.shiftKey) {
				if (document.activeElement === first) {
					e.preventDefault();
					last.focus();
				}
			} else {
				if (document.activeElement === last) {
					e.preventDefault();
					first.focus();
				}
			}
		};

		document.addEventListener('keydown', handler);
		this.state.focusTrap = handler;
		setTimeout(() => getFocusables()[0]?.focus(), 100);
	},

	_removeFocusTrap() {
		if (this.state.focusTrap) {
			document.removeEventListener('keydown', this.state.focusTrap);
			this.state.focusTrap = null;
			if (this.state.triggerElement) {
				this.state.triggerElement.focus();
				this.state.triggerElement = null;
			}
		}
	},
};

/*********************************************************************************************
 * 🌍 GLOBAL EXPOSURE + BACKWARD COMPAT
 */
window.HeaderScrollManager = HeaderScrollManager;
window.MegaMenuManager = MegaMenuManager;
window.MobileNavManager = MobileNavManager;

// Backward compat pour anciens appels (search, selector langue, etc.)
window.mmMobileMenu = () => MobileNavManager.toggle();
window.mmCloseMobileMenu = (keep) => MobileNavManager.close(keep);
window.mmMobileMenuShowNext = () => { /* deprecated */ };
window.mmMobileMenuShowPrev = () => { /* deprecated */ };
window.getMegaMenuPosition = () => { /* deprecated — plus de calcul JS */ };

// Bg filter placeholders (utilisés par search/language selector legacy)
window.showBgFilter = (zIndex) => {
	const filter = document.querySelector('[data-filter]');
	if (!filter) return;
	filter.classList.add('active');
	if (zIndex) filter.style.zIndex = zIndex;
};
window.hideBgFilter = () => {
	const filter = document.querySelector('[data-filter]');
	if (!filter) return;
	filter.classList.remove('active');
	setTimeout(() => filter.style.removeProperty('z-index'), 300);
};

/*********************************************************************************************
 * 🎯 AUTO-INIT
 */
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		HeaderScrollManager.init();
		MegaMenuManager.init();
		MobileNavManager.init();
		HoverScopeManager.init();
	});
} else {
	HeaderScrollManager.init();
	MegaMenuManager.init();
	MobileNavManager.init();
	HoverScopeManager.init();
}
